import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalParamsUtility from '@sparta.global.utilities/sparta-global-params-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';

/**
 * Shared functions for Sparta globals.
 */
class SpartaGlobalFunctions {
  constructor() {
    // console.log('%cstore ', 'color: lime', spartaStore);
    spartaEventsUtility.sub(spartaEventsUtility.EVENTS.SPA_PARAMS_LOADED, spaParams => {
      this.spaParams = spaParams.data;
    });
  }

  /**
   * @param {string} name Name of the cookie to search for
   * @return {string} Cookie Value
   */
  spartaGetCookie(name) {
    let returnvalue = '';
    const search = `${name}=`;
    if (document.cookie.length > 0) {
      let offset = document.cookie.indexOf(search);
      if (offset !== -1) {
        offset += search.length;
        let end = document.cookie.indexOf(';', offset);
        if (end === -1) {
          end = document.cookie.length;
        }
        returnvalue = unescape(document.cookie.substring(offset, end));
      }
    }
    return returnvalue;
  }

  /**
   * @param {string} name Name of the cookie to set
   * @param {string} value Value to set the cookie at
   * @param {string} days Number of days to store the cookie
   * @param {string} domain The domain to store the cookie on/for
   * @return {boolean}
   */
  spartaSetCookie(name = '', value = '', days = false, domain = false) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toGMTString()}`;
    }
    const domainStr = domain ? ';domain=.bankofamerica.com' : '';
    document.cookie = `${name}=${value}${expires}${domainStr}; path=/`;
    return true;
  }

  /**
   * @param {string} name Name of the URL parameter to get
   * @return {string|Null} returns the value of the URL parameter or null if not found
   */
  getUrlParam(name) {
    if (window.location.search.indexOf('?') < 0) {
      return null;
    }
    let result = null;
    const paramList = window.location.search.split('?')[1].split('&');
    const paramObj = {};
    paramList.forEach(paramPair => {
      const splitPair = paramPair.split('=');

      // check for duplicate param names
      if (paramObj[splitPair[0]]) {
        paramObj[splitPair[0]] = [paramObj[splitPair[0]]];
        paramObj[splitPair[0]].push(splitPair[1]);
      } else {
        paramObj[splitPair[0]] = splitPair[1];
      }
    });
    if (paramObj[name]) {
      result = decodeURIComponent(paramObj[name]);
    }
    return result;
  }

  /**
   * getGlobalParams Lookup and return string from global language obj
   * the spaParams object is output in the rendered HTML via layout-support-footer.hbs
   */
  getGlobalParams(key) {
    const globalStrings = spartaGlobalParamsUtility.getSpaParams({
      lookupKey: 'globalJs'
    });
    return globalStrings[key] || '';
  }

  /**
   * getLanguage return global language string
   */
  getLanguage() {
    return this.spaParams.language;
  }

  /**
   * getSpaParams return spaParams object
   * @param {Object} context Context object from helpers.
   *     Allows getting spaParams for HBS helpers/Handlebar.compile helper functions.
   */
  getSpaParams(context) {
    if (context) {
      // Does helper get generated from HBS or JS file
      // Note: Helps determine how to pull page and site params.
      const contextTesting = context ? 'global' in context.data.root : undefined;

      // Check for site data
      // Note: Assuming a utility call, the spaParams would be availble if `contextTesting` is false.
      const spaParams = contextTesting ? context.data.root : this.spaParams;
      return spaParams;
    }
    return this.spaParams;
  }

  /**
   * Get the global strings for use in helper for HBS or module JS helper calls
   * In helper context the languae is already provided while module JS comppile function it is not
   * @param {Object} spaParams Object from helper context or store utility
   * @returns {Object} Strings assigned by language
   */
  getGlobalStrings(spaParams) {
    return spaParams.language in spaParams.global ? spaParams.global[spaParams.language] : spaParams.global;
  }

  /**
   * Reformat phone numbers on mobile devices based on class: '.spa-mobile-tel'
   * @param  {nodelist} context - list of elements to process
   * @example
   *  <span class="spa-fn spa-mobile-tel">980-555-1234</span>
   */
  mobilePhoneFormatHelper(elements) {
    const els = [...elements] || document.querySelectorAll('.spa-mobile-tel');
    els.forEach(el => {
      if (el.getAttribute('data-mobile-phone-formatted')) {
        return;
      }
      let phone = el.innerHTML.replace(/\D/g, '');
      phone = `1${phone}`;
      el.innerHTML = `<a href="tel:+${phone}">${el.innerHTML}</a>`;
      el.setAttribute('data-mobile-phone-formatted', '1');
    });
  }

  /**
   * Loads a script by URL
   * @param {string} url - URL string to load script from.
   * returns a promise
   */
  loadScriptByUrl(url) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    document.body.appendChild(script);
    return new Promise((resolve, reject) => {
      script.onload = resolve;
      script.onerror = reject;
    });
  }

  /**
   * Loads a css by URL
   * @param {string} url - URL string to load css from.
   * returns a promise
   */
  loadCssByUrl(url) {
    const link = document.createElement('link');
    link.href = url;
    link.rel = 'stylesheet';
    link.type = 'text/css';
    document.body.appendChild(link);
    return new Promise((resolve, reject) => {
      link.onload = resolve;
      link.onerror = reject;
    });
  }

  /**
   * Get the HTML blocks between opening and closing of helper tags in HBS file.
   * @param {context} context HTML that is between the item brackets.
   * @param {object} options JSON object used to build the msg group.
   * @returns {void}
   */
  getHelperContext(context, options) {
    // Set block content.
    let content = '';
    if (options && options.isBlockHelperOptions) {
      const blockHelperTemplate = Handlebars.compile(`${options.helperBlockContent}`);
      content = blockHelperTemplate(context);
    }
    if (options && Object.prototype.hasOwnProperty.call(options, 'fn')) {
      content = options.fn(context);
    }
    return content;
  }

  /**
   * Generates a random id that can be used for html elements.
   * @param {String} prepend Optional prepend string. Should always start with a character.
   * @returns {String} Random ID
   */
  generateRandomId(prepend = 'sparta') {
    return `${prepend}-${Math.floor(100000 + Math.random() * 900000)}`;
  }

  /**
   * Gets the absolute path for the site host name
   * @returns {String} Host name URL for the site
   */
  getSpaAssetsUrlPrefix() {
    let prefix = '';
    const spaParams = this.getSpaParams();
    const {
      isWidget
    } = spaParams;
    const {
      cdn = {},
      domain = {},
      widgetAPIEndpoint = ''
    } = spaParams.siteParams;
    function normalizeHost(host, ssl) {
      if (host && !host.toLowerCase().startsWith('http')) {
        host = `http${ssl ? 's' : ''}://${host}`;
      }
      return host;
    }
    if (cdn && cdn.enabled && cdn.host) {
      prefix = normalizeHost(cdn.host, domain && domain.ssl);
    } else {
      if (isWidget && widgetAPIEndpoint) {
        prefix = widgetAPIEndpoint; // widgetApiEndpoint always has the protocol
      } else {
        if (domain && domain.hostname) {
          prefix = normalizeHost(domain.hostname, domain.ssl);
        }
      }
    }
    if (isWidget) {
      prefix = `${prefix}/spa/widgets`;
    }
    return prefix;
  }
}
let instance = new SpartaGlobalFunctions();
//Below code will set the spaParams in this singleton instance for site or widget based on where it is loaded from
try {
  if (document.currentScript) {
    const widgetName = document.currentScript.getAttribute("widget-name");
    const widgetVersion = document.currentScript.getAttribute("widget-version");
    if (widgetName && widgetVersion) {
      instance.spaParams = Object.assign(window.spaWidgetParams[widgetName][widgetVersion], {
        isWidget: true,
        language: document.currentScript.parentElement.querySelector('.sparta-widget-container').getAttribute('data-sparta-lang').split('-')[0]
      });
    } else {
      instance.spaParams = window.spaParams;
    }
  }
} catch (e) {
  console.error('SpartaGlobalFunctions: Failed to process currentScript attributes');
}

export { instance as default };
